import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  url?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  structuredData?: object[];
  breadcrumbs?: Array<{ name: string; url: string }>;
}

const SEO: React.FC<SEOProps> = ({
  title = 'TaskWombat - Premium Commercial & Residential Cleaning Services Across Australia | Professional Services',
  description = 'Australia\'s most trusted and beneficial professional service provider, serving customers across Australia. Fully insured teams providing premium commercial cleaning contracts, residential cleaning, Airbnb cleaning, end of lease cleaning, and specialized cleaning services. Comprehensive $10M public liability coverage. Eco-friendly products. Satisfaction guaranteed. Exclusive seasonal offers. Join customers who prefer to engage with us. Get instant quotes today.',
  keywords = 'commercial cleaning Australia, contract cleaning, cleaning services Australia, professional cleaners, end of lease cleaning, Airbnb cleaning, carpet cleaning, cleaning service near me, same day cleaning, bond back cleaning, eco-friendly cleaning',
  ogImage = 'https://taskwombat.com/og-image.jpg',
  url = 'https://taskwombat.com',
  type = 'website',
  noindex = false,
  structuredData = [],
  breadcrumbs = [],
}) => {
  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TaskWombat',
    url: 'https://taskwombat.com',
    logo: 'https://taskwombat.com/logo.jpg',
    description: 'Professional service provider offering premium commercial cleaning contracts and residential cleaning services across Australia.',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+61-1800-WOMBAT',
      contactType: 'Customer Service',
      areaServed: 'AU',
      availableLanguage: ['English'],
    },
    sameAs: [
      'https://www.facebook.com/share/1GK7uRZZU4/?mibextid=wwXIfr',
      'https://www.instagram.com/taskwombat',
      'https://www.linkedin.com/company/taskwombat',
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'AU',
      addressLocality: 'Tarneit',
      addressRegion: 'VIC',
    },
  };

  // LocalBusiness Schema (Enhanced for Australia)
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://taskwombat.com/#localbusiness',
    name: 'TaskWombat',
    description: description,
    url: url,
    logo: 'https://taskwombat.com/logo.jpg',
    image: ogImage,
    telephone: '+61-1800-WOMBAT',
    email: 'support@taskwombat.com',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'AU',
      addressLocality: 'Tarneit',
      addressRegion: 'VIC',
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Tarneit',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Hoppers Crossing',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Point Cook',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Williams Landing',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Melbourne',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Werribee',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Truganina',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Laverton',
        addressRegion: 'VIC',
      },
      {
        '@type': 'City',
        name: 'Altona Meadows',
        addressRegion: 'VIC',
      },
      {
        '@type': 'Country',
        name: 'Australia',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '1250',
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Sarah Johnson',
        },
        datePublished: '2024-01-15',
        reviewBody: 'TaskWombat\'s end of lease cleaning was outstanding! Got my full bond back. The team was thorough, professional, and cleaned every corner. Highly recommend!',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Michael Chen',
        },
        datePublished: '2024-01-20',
        reviewBody: 'As an Airbnb host, TaskWombat has been a lifesaver. Fast turnaround, always guest-ready, and my property looks amazing. The cleaning quality is consistently excellent.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'TaskWombat Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Residential Moving',
            description: 'Professional residential moving and relocation services',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Office Moving',
            description: 'Commercial office and business relocation services',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Regular House Cleaning',
            description: 'Weekly, fortnightly, and monthly home cleaning services',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'End of Lease Cleaning',
            description: 'Bond-back guaranteed end of lease cleaning service',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Airbnb Cleaning',
            description: 'Fast turnaround Airbnb and short-stay rental cleaning',
          },
        },
      ],
    },
  };

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  } : null;

  // Combine all structured data
  const allStructuredData = [
    organizationSchema,
    localBusinessSchema,
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
    ...structuredData,
  ];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="TaskWombat" />
      <meta name="language" content="en-AU" />
      <meta name="geo.region" content="AU" />
      <meta name="geo.placename" content="Australia" />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="bingbot" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Alternate Languages (if applicable) */}
      <link rel="alternate" hrefLang="en-AU" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="TaskWombat" />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@taskwombat" />
      <meta name="twitter:creator" content="@taskwombat" />

      {/* Additional SEO Meta Tags */}
      <meta name="application-name" content="TaskWombat" />
      <meta name="apple-mobile-web-app-title" content="TaskWombat" />
      <meta name="theme-color" content="#2E9BF5" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Structured Data */}
      {allStructuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;